import {
  Checkbox,
  Ellipsis,
  GenericTable,
  GenericTableVariant,
} from "@visiblethread/react-components";
import clsx from "clsx";
import OverflowPillList from "components/OverflowPillList";
import { checkedTypes } from "constants/types";
import { ReactComponent as CollectionSharedIcon } from "icons/collection-shared.svg";
import { LayersIcon } from "lucide-react";
import { bool } from "prop-types";
import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setSelectedCollections,
  toggleCollectionSelection,
} from "slices/collections/collectionsSlice";
import {
  selectCollections,
  selectHasCategoriesColumn,
  selectSelectedCollections,
} from "slices/collections/selectors";
import { formatDateYMDToDMY, sortByLocaleString } from "utils";
import { getStyles } from "utils/getStyles";

import styles from "./Collections.module.scss";

const { darkGray, middleGray } = getStyles();

const Collections = ({ isAddIn = false }) => {
  const dispatch = useDispatch();
  const collections = useSelector(selectCollections);
  const selectedCollections = useSelector(selectSelectedCollections);

  const selectableCollections = useMemo(
    () => collections.filter((c) => c.files?.length > 0),
    [collections]
  );

  const hasCategoriesColumn = useSelector(selectHasCategoriesColumn);

  const isCollectionSelected = (collection) =>
    selectedCollections.some((c) => c.id === collection.id);

  const getSelectAllState = () => {
    if (selectableCollections.length === 0) {
      return checkedTypes.EMPTY;
    }
    const selectedCount = selectableCollections.filter((c) =>
      isCollectionSelected(c)
    ).length;
    if (selectedCount === 0) {
      return checkedTypes.EMPTY;
    }
    if (selectedCount === selectableCollections.length) {
      return checkedTypes.CHECKED;
    }
    return checkedTypes.INDETERMINATE;
  };

  const handleSelectAll = () => {
    const currentState = getSelectAllState();
    if (currentState === checkedTypes.CHECKED) {
      dispatch(setSelectedCollections([]));
    } else {
      dispatch(setSelectedCollections(selectableCollections));
    }
  };

  const handleCollectionToggle = (collection) => {
    if (collection.files?.length === 0) {
      return;
    }
    dispatch(toggleCollectionSelection(collection));
  };

  const columns = useMemo(
    () => [
      {
        header: () => (
          <Checkbox
            state={getSelectAllState()}
            handleChange={handleSelectAll}
          />
        ),
        id: "checkbox",
        size: 40,
        minSize: 40,
        enableResizing: false,
        enableSorting: false,
        meta: {
          flexGrow: 0,
        },
        cell: ({ row }) => {
          const collection = row.original;
          const isDisabled = collection.files?.length === 0;
          return (
            <Checkbox
              state={
                isCollectionSelected(collection)
                  ? checkedTypes.CHECKED
                  : checkedTypes.EMPTY
              }
              handleChange={() => handleCollectionToggle(collection)}
              disabled={isDisabled}
            />
          );
        },
      },
      {
        header: "Name",
        accessorKey: "name",
        size: 160,
        minSize: 100,
        cell: ({ row }) => {
          const collection = row.original;
          return (
            <div className={styles["collections__name"]}>
              {collection.isShared ? (
                <CollectionSharedIcon
                  className={clsx(
                    collection.files.length === 0 &&
                      styles["collections__shared_icon__disabled"]
                  )}
                  width={14}
                  height={14}
                />
              ) : (
                <LayersIcon width={14} height={14} color={darkGray} />
              )}
              <Ellipsis text={collection.name} />
              <span>({collection.files.length})</span>
            </div>
          );
        },
        sortingFn: sortByLocaleString("name"),
      },
      {
        header: "Shared",
        accessorKey: "isShared",
        size: 75,
        minSize: 75,
        cell: ({ row }) => {
          const collection = row.original;
          return (
            <div className={styles["collections__shared"]}>
              {collection.isShared && <span>✓</span>}
            </div>
          );
        },
        sortingFn: (a, b) =>
          Number(b.original.isShared) - Number(a.original.isShared),
      },
      ...(hasCategoriesColumn
        ? [
            {
              header: "Categories",
              id: "categories",
              size: 200,
              minSize: 150,
              sortDescFirst: false,
              cell: ({ row }) => {
                const { collectionCategories = [] } = row.original;
                if (!collectionCategories.length) {
                  return <Ellipsis text="-" />;
                }
                return (
                  <OverflowPillList
                    items={collectionCategories.map((cat) => ({
                      key: String(cat.typeId),
                      label: cat.typeName,
                      color: middleGray,
                    }))}
                  />
                );
              },
              accessorFn: (row) =>
                row.collectionCategories?.length
                  ? row.collectionCategories
                  : undefined,
              sortUndefined: "last",
              sortingFn: sortByLocaleString(
                (row) => row.original.collectionCategories?.[0]?.typeName
              ),
            },
          ]
        : []),
      ...(!isAddIn
        ? [
            {
              header: "Last used",
              accessorFn: (row) => row.lastUpdated ?? undefined,
              id: "lastUpdated",
              size: 108,
              sortDescFirst: true,
              sortUndefined: "last",
              minSize: 86,
              cell: ({ row }) => {
                const collection = row.original;
                const { lastUpdated } = collection;

                return (
                  <span className={styles["collections__last_updated"]}>
                    <Ellipsis text={formatDateYMDToDMY(lastUpdated)} />
                  </span>
                );
              },
              sortingFn: (rowA, rowB) => {
                const a = rowA.original.lastUpdated;
                const b = rowB.original.lastUpdated;
                return a.localeCompare(b);
              },
            },
          ]
        : []),
    ],
    [selectedCollections, selectableCollections, hasCategoriesColumn, isAddIn]
  );

  const getDefaultSortIndex = () => {
    if (isAddIn) return 1;
    if (hasCategoriesColumn) return 4;
    return 3;
  };

  const defaultSortIndex = getDefaultSortIndex();

  return (
    <GenericTable
      cellClassName={styles.cell}
      className={styles.table}
      data={collections}
      columns={columns}
      disabledMessage="Collection has no files"
      headerCellClassName={styles.headerCell}
      onRowClick={handleCollectionToggle}
      defaultSortColumnIndex={defaultSortIndex}
      isRowDisabled={(row) => row.files.length === 0}
      variant={GenericTableVariant.bordered}
    />
  );
};

Collections.propTypes = {
  isAddIn: bool,
};

export default Collections;
